<script setup lang="ts">
import FilterDialog from '@/components/FilterBuilder/FilterDialog.vue';
import TableHeader from '@/components/DataTable/TableHeader.vue';
import TableHeaderItem from '@/components/DataTable/TableHeaderItem.vue';
import TableHeaderPaginationGroup from '@/components/DataTable/TableHeaderPaginationGroup.vue';
import ListDisplayMenu, { type DisplayLayoutOption } from '@/components/DataTable/ListDisplayMenu.vue';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { Squares2X2Icon } from '@heroicons/vue/24/outline';
import { computed, markRaw } from 'vue';
import TableGridIcon from '@/components/icons/TableGridIcon.vue';
import type { IScope } from '@/types/filterBuilderTypes';
import type { TableLayoutMode } from '@/stores/persistentTableStore';

/**
 * Toolbar for the audio-file-issues page — a slimmer cousin of
 * {@link file:src/components/DataTable/EditableSearchTableHeader.vue}.
 *
 * Filter, then Display, then the pager, as the list toolbar has them. Display
 * holds the Table or Cards layout, which uses a page-local `tableLayout`
 * field so the two preferences stay independent, and "Columns…". Column
 * settings only apply to the table, so they are left out in card layout.
 */

defineProps<{
  totalRecords?: number;
}>();

const {
  setPageIndex,
  setFilter,
  storeKey,
  tableLayout,
  setTableLayout,
  visiblePinnedColumns,
  visibleNonPinnedColumns,
} = usePersistentTable();

/** The test ids are the ones the old view menu used. */
const layoutOptions: DisplayLayoutOption[] = [
  { value: 'rows', label: 'Table', icon: markRaw(TableGridIcon), testId: 'audio-file-issues-layout-rows' },
  { value: 'cards', label: 'Cards', icon: markRaw(Squares2X2Icon), testId: 'audio-file-issues-layout-cards' },
];

const visibleColumnCount = computed(() => visiblePinnedColumns.value.length + visibleNonPinnedColumns.value.length);


function onTableLayoutChange(value: string) {
  // Narrow before persisting, so the store only ever receives the two known modes.
  if (value === 'rows' || value === 'cards') {
    setTableLayout(value as TableLayoutMode);
  }
}


function paginationHandler(pageIndex: number) {
  setPageIndex(pageIndex);
}

function applyFilterHandler(parsedQuery: IScope) {
  setFilter(parsedQuery);
}

// This page's saved views live in the section sidebar, under Reports, as a
// list's do. The toolbar has no saved-view control.
</script>

<template>
  <TableHeader>
    <template #left>
      <TableHeaderItem>
        <FilterDialog
          :key="storeKey"
          data-testid="audio-file-issues-filter-dialog"
          @applyFilter="applyFilterHandler"
        />
      </TableHeaderItem>
      <!-- Display: Table or Cards, and "Columns…" for the table. Columns
           apply to the table only, so cards leave the link out. -->
      <TableHeaderItem>
        <ListDisplayMenu
          :layout-options="layoutOptions"
          :layout="tableLayout"
          :columns-label="tableLayout === 'rows' ? 'Columns…' : null"
          :columns-shown-count="visibleColumnCount"
          columns-test-id="audio-file-issues-column-settings"
          @update:layout="onTableLayoutChange"
          :page-memory-key="storeKey"
        />
      </TableHeaderItem>
    </template>

    <template #right>
      <TableHeaderItem class="min-w-max shrink-0">
        <TableHeaderPaginationGroup
          :total-records="totalRecords"
          total-label="audio file issues"
          @pagination="paginationHandler"
        />
      </TableHeaderItem>
    </template>
  </TableHeader>
</template>
